// RBAC types and interfaces

// ─── Const Enums (as const para tree-shaking) ───────────────────────────

export const Role = {
  ADMIN: 'ADMIN',
  GERENTE: 'GERENTE',
  USUARIO: 'USUARIO',
} as const;
export type Role = (typeof Role)[keyof typeof Role];

export const Scope = {
  READ: 'READ',
  WRITE: 'WRITE',
  EDIT: 'EDIT',
  DELETE: 'DELETE',
  LIST: 'LIST',
} as const;
export type Scope = (typeof Scope)[keyof typeof Scope];

export const Resource = {
  Projects: 'Projects',
  Flows: 'Flows',
  ApiCredentials: 'ApiCredentials',
  Executions: 'Executions',
  Members: 'Members',
  Connections: 'Connections',
  BusinessEntities: 'BusinessEntities',
  Agents: 'Agents',
} as const;
export type Resource = (typeof Resource)[keyof typeof Resource];

// ─── Interfaces ─────────────────────────────────────────────────────────

/** Mapeamento de scopes por recurso */
export type MemberScopes = Partial<Record<Resource, Scope[]>>;

/** Contexto RBAC completo do usuário autenticado */
export interface RbacContext {
  accountId: string | null;
  memberId: string | null;
  role: Role | null;
  scopes: MemberScopes;
  globalAccess: boolean;
}

/** Hierarquia de roles para comparação */
export const ROLE_HIERARCHY: Record<Role, number> = {
  [Role.ADMIN]: 3,
  [Role.GERENTE]: 2,
  [Role.USUARIO]: 1,
};

// ─── Status Types ───────────────────────────────────────────────────────

export type AccountStatus = 'ACTIVE' | 'SUSPENDED' | 'CANCELLED';
export type MemberStatus = 'ACTIVE' | 'INVITED' | 'DISABLED';
export type AuditAction = 'CREATE' | 'EDIT' | 'DELETE';
export type AuditEntityType = 'MEMBER' | 'SCOPE' | 'ACCOUNT' | 'FLOW' | 'EXECUTION' | 'PROJECT' | 'CONNECTION' | 'CREDENTIAL';

// ─── API Response Types ─────────────────────────────────────────────────

export interface AccountResponse {
  id: string;
  name: string;
  status: AccountStatus;
  planCode: string;
  memberCount: number;
  financialEmail: string | null;
  cnpj: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MemberResponse {
  id: string;
  userId: string;
  name: string;
  email: string;
  role: Role;
  status: MemberStatus;
  scopes: MemberScopes;
  createdAt: string;
  updatedAt: string;
}

export interface AuditLogResponse {
  id: string;
  action: AuditAction;
  entityType: AuditEntityType;
  entityId: string | null;
  entityName: string | null;
  targetMemberId: string | null;
  executorMemberId: string;
  executorName: string;
  targetName: string | null;
  changes: AuditChange[];
  timestamp: string;
}

export interface AuditChange {
  field: string;
  oldValue: unknown;
  newValue: unknown;
}

// ─── API Request Types ──────────────────────────────────────────────────

export interface CreateMemberRequest {
  email: string;
  fullName: string;
  role: 'GERENTE' | 'USUARIO';
  scopes?: MemberScopes;
}

export interface UpdateScopesRequest {
  scopes: MemberScopes;
}

export interface UpdateRoleRequest {
  role: 'GERENTE' | 'USUARIO';
}

export interface UpdateAccountStatusRequest {
  status: AccountStatus;
}

export interface AdminUpdateMemberRequest {
  name?: string;
  email?: string;
  role?: Role;
  scopes?: MemberScopes;
}

// ─── Utility Types ──────────────────────────────────────────────────────

export interface PaginatedResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  page: number;
  size: number;
}

export interface AuditLogFilters {
  action?: AuditAction;
  targetMemberId?: string;
  from?: string;
  to?: string;
  page?: number;
  size?: number;
}

export interface AdminAccountFilters {
  status?: AccountStatus;
  planCode?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  size?: number;
}
